import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Sparkles, X, Loader2, ArrowRight, ArrowLeft, Send, Wand2, Pencil,
} from 'lucide-react';
import AssigneeContacts from './AssigneeContacts';
import VoiceRecorder from './VoiceRecorder';
import WorkOrderAttachments from './WorkOrderAttachments';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authAxios = axios.create();
authAxios.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('adminToken');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const PRIORITIES = ['Low', 'Med', 'High', 'Urgent'];
const PRIORITY_COLORS = {
  Low: 'text-slate-200 bg-slate-500/25 border-slate-400/40',
  Med: 'text-yellow-200 bg-yellow-500/25 border-yellow-400/40',
  High: 'text-red-200 bg-red-500/25 border-red-400/40',
  Urgent: 'text-red-50 bg-red-600/50 border-red-400/70',
};
const DEPARTMENTS = ['Marketing', 'Driver', 'Finance', 'Sales', 'Operations', 'Technology', 'Legal'];

const EXAMPLES = [
  'Marketing needs a flyer and social post for Acme Health’s new Dallas wheelchair route — urgent, by next Friday',
  'Fleet needs 3 dash cameras installed on the new Houston vans at the Houston yard this month',
  'Finance needs the Q3 broker reconciliation pulled together for the captive audit by the 15th',
];

const STEP_LABELS = ['Describe', 'Review', 'Assign & Send'];

const Label = ({ children }) => (
  <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">{children}</label>
);

export const WorkOrderAIWizard = ({ open, onClose, onCreated }) => {
  const [step, setStep] = useState(1);
  const [description, setDescription] = useState('');
  const [thinking, setThinking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [draft, setDraft] = useState(null);
  const [summary, setSummary] = useState('');
  const [confidence, setConfidence] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [requester, setRequester] = useState({ name: '', contact_email: '', contact_phone: '' });

  const reset = () => {
    setStep(1); setDescription(''); setDraft(null); setSummary(''); setConfidence(null);
    setSuggestions([]); setAssignees([]); setAttachments([]);
    setRequester({ name: '', contact_email: '', contact_phone: '' });
  };

  const close = () => { reset(); onClose(); };

  if (!open) return null;

  const analyze = async () => {
    if (description.trim().length < 10) {
      toast.error('Add a bit more detail (at least 10 characters)');
      return;
    }
    setThinking(true);
    try {
      const { data } = await authAxios.post(`${API}/work-orders/ai/draft`, {
        description: description.trim(),
        requester_name: requester.name || null,
      });
      const d = data.draft || {};
      setDraft(d);
      setSummary(d.summary || '');
      setConfidence(d.confidence ?? null);
      setSuggestions(data.suggested_assignees || []);
      if (d.name && !requester.name) setRequester((r) => ({ ...r, name: d.name }));
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI could not draft this. Try rephrasing.');
    } finally {
      setThinking(false);
    }
  };

  const toggleDept = (dept) => {
    setDraft((d) => ({
      ...d,
      departments_needed: d.departments_needed.includes(dept)
        ? d.departments_needed.filter((x) => x !== dept)
        : [...d.departments_needed, dept],
    }));
  };

  const submit = async () => {
    if (!requester.name.trim()) { toast.error('Your name is required'); return; }
    if (!draft.departments_needed?.length) { toast.error('Pick at least one department needed'); return; }
    setSubmitting(true);
    try {
      const payload = {
        request_date: draft.request_date,
        priority: draft.priority,
        name: requester.name.trim(),
        requested_by_department: draft.requested_by_department,
        business_client: draft.business_client,
        departments_needed: draft.departments_needed,
        location_for_service: draft.location_for_service,
        instructions: draft.instructions,
        date_due: draft.date_due,
        work_description: draft.work_description,
        contact_email: requester.contact_email || null,
        contact_phone: requester.contact_phone || null,
        assignee_contacts: assignees,
        attachments,
        attachment_urls: [],
      };
      const { data } = await authAxios.post(`${API}/work-orders`, payload);
      toast.success(
        assignees.length
          ? `${data.wo_number} created · ${assignees.length} assignee${assignees.length > 1 ? 's' : ''} emailed`
          : `${data.wo_number} created`
      );
      onCreated?.(data);
      close();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create work order');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex sm:items-start sm:justify-center overflow-y-auto"
      data-testid="wo-ai-wizard"
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full sm:max-w-2xl sm:my-8 min-h-full sm:min-h-0 bg-[#0b1120] sm:rounded-2xl border-0 sm:border border-white/10 flex flex-col"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#0b1120]/95 backdrop-blur px-4 sm:px-6 py-4 border-b border-white/10">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-fuchsia-500/30 to-cyan-500/30 border border-white/10 flex items-center justify-center shrink-0">
                <Sparkles className="w-4 h-4 text-fuchsia-300" />
              </div>
              <div className="min-w-0">
                <h2 className="text-base sm:text-lg font-semibold text-white truncate">AI Work Order</h2>
                <p className="text-xs text-slate-400">Step {step} of 3 · {STEP_LABELS[step - 1]}</p>
              </div>
            </div>
            <button
              onClick={close}
              className="w-11 h-11 -mr-2 flex items-center justify-center text-slate-400 hover:text-white rounded-lg"
              aria-label="Close"
              data-testid="wo-ai-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Progress */}
          <div className="flex gap-1.5 mt-4">
            {[1, 2, 3].map((n) => (
              <div
                key={n}
                className={`h-1.5 flex-1 rounded-full transition-colors ${n <= step ? 'bg-gradient-to-r from-fuchsia-400 to-cyan-400' : 'bg-white/10'}`}
              />
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 px-4 sm:px-6 py-5 space-y-5">
          {step === 1 && (
            <>
              <div>
                <h3 className="text-white text-base font-medium">What do you need done?</h3>
                <p className="text-sm text-slate-400 mt-1">
                  Type it however you'd say it out loud. AI fills in the whole work order for you.
                </p>
              </div>
              <textarea
                rows={6}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g. Need marketing to build a flyer for Acme Health's new Dallas route, urgent, by next Friday…"
                className="input-dark w-full text-base leading-relaxed"
                data-testid="wo-ai-description"
              />
              <VoiceRecorder
                onTranscript={(text) => setDescription((d) => (d ? `${d.trim()} ${text}` : text))}
              />
              <div>
                <p className="text-xs text-slate-500 mb-2">Snap a photo of what you're talking about (optional)</p>
                <WorkOrderAttachments attachments={attachments} onChange={setAttachments} />
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-2">Or start from an example</p>
                <div className="space-y-2">
                  {EXAMPLES.map((ex) => (
                    <button
                      key={ex}
                      type="button"
                      onClick={() => setDescription(ex)}
                      className="w-full text-left text-xs sm:text-sm text-slate-300 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-3 py-3 min-h-[44px]"
                      data-testid="wo-ai-example"
                    >
                      {ex}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {step === 2 && draft && (
            <>
              <div className="rounded-xl border border-fuchsia-400/25 bg-fuchsia-500/10 p-3.5">
                <div className="flex items-center gap-2 mb-1.5">
                  <Wand2 className="w-3.5 h-3.5 text-fuchsia-300" />
                  <span className="text-[11px] uppercase tracking-wider text-fuchsia-200 font-semibold">What AI understood</span>
                  {confidence != null && (
                    <span className="ml-auto text-[11px] text-slate-300">{Math.round(confidence * 100)}% confident</span>
                  )}
                </div>
                <p className="text-sm text-slate-200" data-testid="wo-ai-summary">{summary || '—'}</p>
              </div>

              <p className="text-xs text-slate-500 flex items-center gap-1.5">
                <Pencil className="w-3 h-3" /> Everything below is editable — tweak anything that's off.
              </p>

              <div>
                <Label>Priority</Label>
                <div className="grid grid-cols-4 gap-2">
                  {PRIORITIES.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setDraft({ ...draft, priority: p })}
                      className={`min-h-[44px] rounded-lg text-sm border transition-all ${draft.priority === p ? PRIORITY_COLORS[p] : 'border-white/10 text-slate-400'}`}
                      data-testid={`wo-ai-priority-${p}`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Business / Client</Label>
                  <input
                    type="text" value={draft.business_client}
                    onChange={(e) => setDraft({ ...draft, business_client: e.target.value })}
                    className="input-dark w-full min-h-[44px]" data-testid="wo-ai-client"
                  />
                </div>
                <div>
                  <Label>Requested By (Dept)</Label>
                  <select
                    value={draft.requested_by_department}
                    onChange={(e) => setDraft({ ...draft, requested_by_department: e.target.value })}
                    className="input-dark w-full min-h-[44px]" data-testid="wo-ai-req-dept"
                  >
                    {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Location for Service</Label>
                  <input
                    type="text" value={draft.location_for_service}
                    onChange={(e) => setDraft({ ...draft, location_for_service: e.target.value })}
                    className="input-dark w-full min-h-[44px]" data-testid="wo-ai-location"
                  />
                </div>
                <div>
                  <Label>Date Due</Label>
                  <input
                    type="date" value={draft.date_due}
                    onChange={(e) => setDraft({ ...draft, date_due: e.target.value })}
                    className="input-dark w-full min-h-[44px]" data-testid="wo-ai-due"
                  />
                </div>
              </div>

              <div>
                <Label>Departments Needed</Label>
                <div className="flex flex-wrap gap-2">
                  {DEPARTMENTS.map((d) => {
                    const on = draft.departments_needed?.includes(d);
                    return (
                      <button
                        key={d} type="button" onClick={() => toggleDept(d)}
                        className={`px-3.5 min-h-[40px] rounded-full text-sm border transition-all ${on ? 'bg-cyan-500/20 text-cyan-200 border-cyan-400/40' : 'border-white/10 text-slate-400'}`}
                        data-testid={`wo-ai-dept-${d}`}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label>Work Description</Label>
                <textarea
                  rows={3} value={draft.work_description}
                  onChange={(e) => setDraft({ ...draft, work_description: e.target.value })}
                  className="input-dark w-full" data-testid="wo-ai-description-field"
                />
              </div>
              <div>
                <Label>Instructions</Label>
                <textarea
                  rows={4} value={draft.instructions}
                  onChange={(e) => setDraft({ ...draft, instructions: e.target.value })}
                  className="input-dark w-full" data-testid="wo-ai-instructions"
                />
              </div>
            </>
          )}

          {step === 3 && draft && (
            <>
              <div>
                <h3 className="text-white text-base font-medium">Who's doing it, and who are you?</h3>
                <p className="text-sm text-slate-400 mt-1">Assignees get emailed immediately and on every status change.</p>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/5 p-3.5">
                <Label>Assignee Contacts</Label>
                <AssigneeContacts contacts={assignees} onChange={setAssignees} suggestions={suggestions} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Your Name *</Label>
                  <input
                    type="text" value={requester.name}
                    onChange={(e) => setRequester({ ...requester, name: e.target.value })}
                    placeholder="Full name" className="input-dark w-full min-h-[44px]" data-testid="wo-ai-requester-name"
                  />
                </div>
                <div>
                  <Label>Your Email</Label>
                  <input
                    type="email" value={requester.contact_email}
                    onChange={(e) => setRequester({ ...requester, contact_email: e.target.value })}
                    placeholder="you@medexova.com" className="input-dark w-full min-h-[44px]" data-testid="wo-ai-requester-email"
                  />
                </div>
              </div>

              <div className="rounded-xl border border-white/10 bg-black/30 p-3.5 text-sm space-y-1.5">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Summary</div>
                <div className="text-white font-medium">{draft.business_client} · {draft.priority} · due {draft.date_due}</div>
                <div className="text-slate-400 text-xs">{draft.departments_needed?.join(', ')} · {draft.location_for_service}</div>
                <div className="text-slate-300 text-xs pt-1">{draft.work_description}</div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-[#0b1120]/95 backdrop-blur border-t border-white/10 px-4 sm:px-6 py-3.5 flex items-center gap-3">
          {step > 1 ? (
            <button
              onClick={() => setStep(step - 1)}
              className="min-h-[48px] px-4 rounded-xl text-slate-300 hover:text-white hover:bg-white/5 flex items-center gap-1.5"
              data-testid="wo-ai-back"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          ) : (
            <button onClick={close} className="min-h-[48px] px-4 rounded-xl text-slate-400 hover:text-white">Cancel</button>
          )}

          <div className="flex-1" />

          {step === 1 && (
            <button
              onClick={analyze}
              disabled={thinking}
              className="min-h-[48px] px-5 rounded-xl bg-gradient-to-r from-fuchsia-500 to-cyan-400 text-black font-semibold disabled:opacity-60 flex items-center gap-2"
              data-testid="wo-ai-analyze"
            >
              {thinking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {thinking ? 'Drafting…' : 'Draft with AI'}
            </button>
          )}
          {step === 2 && (
            <button
              onClick={() => setStep(3)}
              className="min-h-[48px] px-5 rounded-xl bg-cyan-500 text-black font-semibold flex items-center gap-2"
              data-testid="wo-ai-next"
            >
              Looks good <ArrowRight className="w-4 h-4" />
            </button>
          )}
          {step === 3 && (
            <button
              onClick={submit}
              disabled={submitting}
              className="min-h-[48px] px-5 rounded-xl bg-emerald-500 text-black font-semibold disabled:opacity-60 flex items-center gap-2"
              data-testid="wo-ai-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Create & Notify
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default WorkOrderAIWizard;
